import dayjs from "dayjs";
import { loadTesseract } from "../../utils/lazyClientModules";

function safeStr(v) {
  return String(v ?? "").trim();
}

function normalizeWhitespace(v) {
  return safeStr(v).replace(/\s+/g, " ").trim();
}

function normalizeMultiline(v) {
  return String(v ?? "")
    .replace(/\r/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function toNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function safeFilePart(v) {
  return String(v || "")
    .trim()
    .replace(/[\/\\:*?"<>|]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function splitName(fullName) {
  const full = normalizeWhitespace(fullName);
  if (!full) return { first: "", last: "", full: "" };
  const parts = full.split(" ").filter(Boolean);
  if (parts.length === 1) return { first: parts[0], last: "", full };
  return {
    first: parts.slice(0, -1).join(" "),
    last: parts.slice(-1).join(" "),
    full,
  };
}

function parseMoney(rawValue) {
  const cleaned = String(rawValue || "").replace(/[^0-9.,-]/g, "");
  if (!cleaned) return 0;
  const numeric = cleaned.includes(",") && !cleaned.includes(".")
    ? cleaned.replace(/,/g, ".")
    : cleaned.replace(/,/g, "");
  return toNumber(numeric);
}

function tryParseDate(rawValue) {
  const v = safeStr(rawValue);
  if (!v) return "";
  const candidates = [
    "YYYY-MM-DD",
    "YYYY/MM/DD",
    "MM/DD/YYYY",
    "DD/MM/YYYY",
    "MMM D, YYYY",
    "MMMM D, YYYY",
    "D MMM YYYY",
    "D MMMM YYYY",
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const parsed = dayjs(v, candidates[i], true);
    if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
  }

  const autoParsed = dayjs(v);
  if (autoParsed.isValid()) return autoParsed.format("YYYY-MM-DD");
  return "";
}

function normalizeProvince(value) {
  const input = String(value || "").trim().toLowerCase();
  if (!input) return "";
  if (input === "on" || input === "ontario") return "ON";
  if (input === "mb" || input === "manitoba") return "MB";
  if (input === "ab" || input === "alberta") return "AB";
  if (input === "sk" || input === "saskatchewan") return "SK";
  if (input === "bc" || input === "british columbia") return "BC";
  return input.toUpperCase();
}

const DAY_WORD_TO_NUMBER = {
  first: "1",
  second: "2",
  third: "3",
  fourth: "4",
  fifth: "5",
  sixth: "6",
  seventh: "7",
  eighth: "8",
  ninth: "9",
  tenth: "10",
  eleventh: "11",
  twelfth: "12",
  thirteenth: "13",
  fourteenth: "14",
  fifteenth: "15",
  sixteenth: "16",
  seventeenth: "17",
  eighteenth: "18",
  nineteenth: "19",
  twentieth: "20",
  twentyfirst: "21",
  twentysecond: "22",
  twentythird: "23",
  twentyfourth: "24",
  twentyfifth: "25",
  twentysixth: "26",
  twentyseventh: "27",
  twentyeighth: "28",
  twentyninth: "29",
  thirtieth: "30",
  thirtyfirst: "31",
};

const DATE_FRAGMENT =
  "(?:\\d{4}[/-]\\d{1,2}[/-]\\d{1,2}|\\d{1,2}[/-]\\d{1,2}[/-]\\d{2,4}|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\\s+\\d{1,2},?\\s+\\d{2,4}|\\d{1,2}\\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\\s+\\d{2,4})";

function normalizeFieldKey(v) {
  return String(v || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

function escapeRegex(v) {
  return String(v || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function parseRentDay(rawValue) {
  const value = safeStr(rawValue).toLowerCase();
  if (!value) return "";

  const numeric = value.match(/\b([0-9]{1,2})(?:st|nd|rd|th)?\b/i);
  if (numeric) {
    const asNum = Number(numeric[1]);
    if (asNum >= 1 && asNum <= 31) return String(asNum);
  }

  const wordKey = value.replace(/[^a-z]/g, "");
  return DAY_WORD_TO_NUMBER[wordKey] || "";
}

function collectDateValues(text) {
  const source = String(text || "");
  const matches = source.match(new RegExp(DATE_FRAGMENT, "gi")) || [];
  const out = [];
  const seen = new Set();

  for (let i = 0; i < matches.length; i += 1) {
    const parsed = tryParseDate(matches[i]);
    if (!parsed || seen.has(parsed)) continue;
    seen.add(parsed);
    out.push(parsed);
    if (out.length >= 12) break;
  }
  return out;
}

function pickDateByLabels(text, labels = []) {
  const source = String(text || "");
  for (let i = 0; i < labels.length; i += 1) {
    const label = labels[i];
    if (!label) continue;
    const normalizedLabel = escapeRegex(label).replace(/\s+/g, "\\s+");
    const regex = new RegExp(
      `${normalizedLabel}[\\s:\\-]{0,12}(${DATE_FRAGMENT})`,
      "i"
    );
    const match = source.match(regex);
    if (!match) continue;
    const parsed = tryParseDate(match[1]);
    if (parsed) return parsed;
  }
  return "";
}

function normalizeFormFieldValue(value) {
  if (Array.isArray(value)) {
    return normalizeWhitespace(
      value
        .map((v) => safeStr(v))
        .filter(Boolean)
        .join(" ")
    );
  }
  if (value == null) return "";
  if (typeof value === "boolean") return value ? "yes" : "no";
  return normalizeWhitespace(value);
}

function pickFormFieldValue(formFields, aliases = [], excludes = []) {
  const entries = Object.entries(formFields || {});
  if (!entries.length) return "";

  const aliasKeys = aliases.map((v) => normalizeFieldKey(v)).filter(Boolean);
  const excludeKeys = excludes.map((v) => normalizeFieldKey(v)).filter(Boolean);
  let bestValue = "";
  let bestScore = -1;

  for (let i = 0; i < entries.length; i += 1) {
    const [rawKey, rawValue] = entries[i];
    const keyNorm = normalizeFieldKey(rawKey);
    if (!keyNorm) continue;
    if (excludeKeys.some((k) => k && keyNorm.includes(k))) continue;

    let score = 0;
    for (let a = 0; a < aliasKeys.length; a += 1) {
      const alias = aliasKeys[a];
      if (alias && keyNorm.includes(alias)) {
        score += Math.max(3, alias.length);
      }
    }
    if (score <= 0) continue;

    const value = normalizeFormFieldValue(rawValue);
    if (!value) continue;

    if (score > bestScore) {
      bestScore = score;
      bestValue = value;
    }
  }

  return bestValue;
}

function cleanupNameCandidate(value) {
  const clean = normalizeWhitespace(value);
  if (!clean) return "";
  if (/\d/.test(clean)) return "";
  const trimmed = clean
    .replace(/\b(address|email|phone|suite|unit|date)\b.*$/i, "")
    .replace(/[,;:\-]+$/, "")
    .trim();
  if (!trimmed || trimmed.length < 3) return "";
  return trimmed;
}

function cleanupSuiteCandidate(value) {
  const clean = safeStr(value);
  if (!clean) return "";
  return clean
    .replace(/^(suite|unit|apartment|apt\.?)\s*/i, "")
    .replace(/^#/, "")
    .replace(/[,\s]+$/, "")
    .trim();
}

function pickFirstMatch(text, patterns = []) {
  const source = String(text || "");
  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];
    const match = source.match(pattern);
    if (!match) continue;
    const value = normalizeWhitespace(match[1] || "");
    if (value) return value;
  }
  return "";
}

function pickEmail(text) {
  return pickFirstMatch(text, [
    /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i,
  ]);
}

function detectByKeyword(rawText, propertyProvince) {
  const text = String(rawText || "").toLowerCase();
  const province = normalizeProvince(propertyProvince);
  if (
    text.includes("standard form of lease") ||
    text.includes("residential tenancies act, 2006") ||
    text.includes("ontario")
  ) {
    return { source: "ontario", confidence: 0.9, jurisdiction: "ON" };
  }
  if (
    text.includes("manitoba") ||
    text.includes("standard residential tenancy agreement") ||
    text.includes("residential tenancies branch")
  ) {
    return { source: "manitoba", confidence: 0.85, jurisdiction: "MB" };
  }
  if (
    text.includes("alberta") ||
    text.includes("residential tenancy agreement") ||
    text.includes("service alberta")
  ) {
    return { source: "alberta", confidence: 0.85, jurisdiction: "AB" };
  }

  if (province === "ON") return { source: "ontario", confidence: 0.55, jurisdiction: "ON" };
  if (province === "MB") return { source: "manitoba", confidence: 0.55, jurisdiction: "MB" };
  if (province === "AB") return { source: "alberta", confidence: 0.55, jurisdiction: "AB" };

  return { source: "monthly", confidence: 0.35, jurisdiction: province || null };
}

async function loadPdfJs() {
  // webpack entry wires workerPort with a bundled module worker.
  // This avoids broken workerSrc URLs in CRA builds.
  return import("pdfjs-dist/webpack.mjs");
}

function buildPageTextFromContentItems(textContent) {
  const items = Array.isArray(textContent?.items) ? textContent.items : [];
  if (!items.length) return "";

  const positioned = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    const str = normalizeWhitespace(item?.str || "");
    if (!str) continue;
    const transform = Array.isArray(item?.transform) ? item.transform : [];
    const x = Number(transform[4] || 0);
    const y = Number(transform[5] || 0);
    positioned.push({ str, x, y });
  }

  if (!positioned.length) return "";

  positioned.sort((a, b) => {
    if (Math.abs(b.y - a.y) > 1.5) return b.y - a.y;
    return a.x - b.x;
  });

  const lines = [];
  const yTolerance = 2.2;

  for (let i = 0; i < positioned.length; i += 1) {
    const token = positioned[i];
    const current = lines[lines.length - 1];
    if (!current || Math.abs(current.y - token.y) > yTolerance) {
      lines.push({ y: token.y, tokens: [token] });
      continue;
    }
    current.tokens.push(token);
  }

  const rendered = lines
    .map((line) => {
      line.tokens.sort((a, b) => a.x - b.x);
      let buffer = "";
      for (let i = 0; i < line.tokens.length; i += 1) {
        const piece = line.tokens[i].str;
        if (!piece) continue;
        if (!buffer) {
          buffer = piece;
          continue;
        }

        const prevChar = buffer.slice(-1);
        const nextChar = piece[0];
        const needsSpace =
          !/[\s([/{-]/.test(prevChar) &&
          !/[)\],.;:!?]/.test(nextChar) &&
          prevChar !== "/" &&
          nextChar !== "/";
        buffer += `${needsSpace ? " " : ""}${piece}`;
      }
      return normalizeWhitespace(buffer);
    })
    .filter(Boolean);

  return normalizeMultiline(rendered.join("\n"));
}

async function extractPdfFormFields(pdf) {
  if (!pdf || typeof pdf.getFieldObjects !== "function") return {};

  try {
    const fieldObjects = await pdf.getFieldObjects();
    if (!fieldObjects || typeof fieldObjects !== "object") return {};

    const out = {};
    for (const [rawKey, entries] of Object.entries(fieldObjects)) {
      const key = safeStr(rawKey);
      if (!key) continue;
      const list = Array.isArray(entries) ? entries : [entries];
      let value = "";

      for (let i = 0; i < list.length; i += 1) {
        const item = list[i] || {};
        const candidate = normalizeFormFieldValue(
          item?.value ??
            item?.fieldValue ??
            item?.defaultValue ??
            item?.exportValue ??
            item?.buttonValue ??
            ""
        );
        if (!candidate) continue;
        value = candidate;
        break;
      }

      if (!value) continue;
      out[key] = value;
    }

    return out;
  } catch {
    return {};
  }
}

async function ocrCanvas(canvas, onProgress, pageNumber = null, totalPages = null) {
  const Tesseract = await loadTesseract();
  const result = await Tesseract.recognize(canvas, "eng", {
    logger: (message) => {
      if (typeof onProgress === "function") {
        const progress = Number(message?.progress || 0);
        onProgress({
          stage: "ocr",
          pageNumber,
          totalPages,
          progress: Number.isFinite(progress) ? progress : 0,
          label: String(message?.status || ""),
        });
      }
    },
  });

  return normalizeWhitespace(result?.data?.text || "");
}

function canvasToJpegDataUrl(canvas, options = {}) {
  if (!canvas || typeof canvas.toDataURL !== "function") return "";
  const maxWidth = Number.isFinite(Number(options.maxWidth))
    ? Math.max(480, Number(options.maxWidth))
    : 1280;
  const quality = Number.isFinite(Number(options.quality))
    ? Math.min(0.92, Math.max(0.4, Number(options.quality)))
    : 0.7;

  const srcWidth = Number(canvas.width || 0);
  const srcHeight = Number(canvas.height || 0);
  if (srcWidth <= 0 || srcHeight <= 0) return "";

  if (srcWidth <= maxWidth) {
    return canvas.toDataURL("image/jpeg", quality);
  }

  const scale = maxWidth / srcWidth;
  const target = document.createElement("canvas");
  target.width = Math.max(1, Math.round(srcWidth * scale));
  target.height = Math.max(1, Math.round(srcHeight * scale));
  const ctx = target.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, 0, 0, target.width, target.height);
  return target.toDataURL("image/jpeg", quality);
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read file data URL."));
    reader.readAsDataURL(file);
  });
}

export async function extractLeaseTextFromFile(file, options = {}) {
  const {
    onProgress,
    maxPdfPages = 20,
    forceOcrForPdf = false,
    capturePageImages = false,
    maxImagePages = 6,
    imageMaxWidth = 1280,
    imageQuality = 0.7,
  } = options || {};

  if (!file) throw new Error("No file provided.");

  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  const isPdf = mime.includes("pdf") || name.endsWith(".pdf");
  const isImage = mime.startsWith("image/") || /\.(png|jpe?g|bmp|gif|tiff?)$/i.test(name);

  if (!isPdf && !isImage) {
    throw new Error("Unsupported file type. Upload PDF or image.");
  }

  if (isImage) {
    const Tesseract = await loadTesseract();
    const { data } = await Tesseract.recognize(file, "eng", {
      logger: (message) => {
        if (typeof onProgress === "function") {
          const progress = Number(message?.progress || 0);
          onProgress({
            stage: "ocr",
            pageNumber: 1,
            totalPages: 1,
            progress: Number.isFinite(progress) ? progress : 0,
            label: String(message?.status || ""),
          });
        }
      },
    });
    let imageDataUrl = "";
    if (capturePageImages) {
      try {
        imageDataUrl = await readFileAsDataUrl(file);
      } catch {
        imageDataUrl = "";
      }
    }

    return {
      text: normalizeMultiline(data?.text || ""),
      method: "image_ocr",
      pageCount: 1,
      ocrUsed: true,
      formFields: {},
      pageImages: imageDataUrl ? [{ page: 1, dataUrl: imageDataUrl }] : [],
    };
  }

  const pdfjs = await loadPdfJs();
  const buffer = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(buffer) });
  const pdf = await loadingTask.promise;
  const formFields = await extractPdfFormFields(pdf);
  const pageCount = pdf.numPages || 0;
  const cappedPages = Math.min(pageCount, Math.max(1, Number(maxPdfPages) || 1));
  const maxPageImagesToCapture = Math.max(0, Number(maxImagePages) || 0);

  let textBlocks = [];
  let ocrUsed = false;
  const pageImages = [];

  for (let pageNumber = 1; pageNumber <= cappedPages; pageNumber += 1) {
    if (typeof onProgress === "function") {
      onProgress({
        stage: "pdf_extract",
        pageNumber,
        totalPages: cappedPages,
        progress: pageNumber / cappedPages,
        label: "Extracting page text",
      });
    }

    const page = await pdf.getPage(pageNumber);
    let pageText = "";

    try {
      const textContent = await page.getTextContent();
      const structured = buildPageTextFromContentItems(textContent);
      if (structured) {
        pageText = structured;
      } else {
        pageText = normalizeWhitespace(
          (textContent?.items || [])
            .map((item) => String(item?.str || ""))
            .join(" ")
        );
      }
    } catch {
      pageText = "";
    }

    const needsOcr = forceOcrForPdf || pageText.length < 40;
    const shouldCapturePageImage = capturePageImages && pageImages.length < maxPageImagesToCapture;
    let renderedCanvas = null;

    if (needsOcr || shouldCapturePageImage) {
      const viewport = page.getViewport({ scale: 2 });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      await page.render({ canvasContext: ctx, viewport }).promise;
      renderedCanvas = canvas;
    }

    if (needsOcr && renderedCanvas) {
      ocrUsed = true;
      const ocrText = await ocrCanvas(renderedCanvas, onProgress, pageNumber, cappedPages);
      if (ocrText) {
        pageText = pageText
          ? `${pageText}\n${ocrText}`
          : ocrText;
      }
    }

    if (shouldCapturePageImage && renderedCanvas) {
      const dataUrl = canvasToJpegDataUrl(renderedCanvas, {
        maxWidth: imageMaxWidth,
        quality: imageQuality,
      });
      if (dataUrl) {
        pageImages.push({
          page: pageNumber,
          dataUrl,
        });
      }
    }

    if (pageText) textBlocks.push(`Page ${pageNumber}\n${pageText}`);
  }

  return {
    text: normalizeMultiline(textBlocks.join("\n\n")),
    method: ocrUsed ? "pdf_text_plus_ocr" : "pdf_text",
    pageCount: cappedPages,
    ocrUsed,
    formFields,
    pageImages,
  };
}

export function buildInitialLeaseImportDraft(rawText, options = {}) {
  const text = normalizeMultiline(rawText);
  const propertyProvince = options?.propertyProvince || "";
  const formFields =
    options?.formFields && typeof options.formFields === "object"
      ? options.formFields
      : {};
  const formValuesText = Object.values(formFields)
    .map((v) => normalizeFormFieldValue(v))
    .filter(Boolean)
    .join(" ");
  const detected = detectByKeyword(`${text}\n${formValuesText}`, propertyProvince);

  const formTenantFull = cleanupNameCandidate(
    pickFormFieldValue(
      formFields,
      [
        "tenantFullName",
        "tenantName",
        "tenant1Name",
        "lesseeName",
        "lessee",
        "occupantName",
        "residentName",
      ],
      ["landlord", "manager", "owner"]
    )
  );
  const formTenantEmail = pickEmail(
    pickFormFieldValue(
      formFields,
      ["tenantEmail", "emailTenant", "residentEmail", "lesseeEmail", "email"],
      ["landlord", "manager", "owner"]
    )
  );
  const formSuiteNumber = cleanupSuiteCandidate(
    pickFormFieldValue(
      formFields,
      ["suiteNumber", "unitNumber", "apartment", "unit", "suite", "rentalUnit"],
      ["landlord", "manager"]
    )
  );
  const formStartDate = tryParseDate(
    pickFormFieldValue(
      formFields,
      [
        "startDate",
        "commencementDate",
        "moveInDate",
        "termStartDate",
        "fromDate",
        "leaseStart",
      ],
      ["signature", "birth"]
    )
  );
  const formEndDate = tryParseDate(
    pickFormFieldValue(
      formFields,
      ["endDate", "expiryDate", "fixedEndDate", "termEndDate", "toDate", "leaseEnd"],
      ["signature", "birth"]
    )
  );
  const formBaseRent = parseMoney(
    pickFormFieldValue(
      formFields,
      ["baseRent", "monthlyRent", "rentAmount", "lawfulRent", "rent"],
      ["deposit", "parking", "discount", "key"]
    )
  );
  const formSecurityDeposit = parseMoney(
    pickFormFieldValue(
      formFields,
      ["securityDeposit", "damageDeposit", "rentDeposit", "depositAmount", "deposit"],
      ["parking", "pet", "key"]
    )
  );
  const formRentDay = parseRentDay(
    pickFormFieldValue(formFields, ["rentDay", "dueDay", "rentDueDay", "rentDue"])
  );

  const tenantFullFromText = cleanupNameCandidate(
    pickFirstMatch(text, [
      /(?:tenant(?:\(s\))?|lessee(?:s)?|occupant(?:s)?|resident(?:s)?)\s*(?:\(?(?:full\s+legal\s+)?names?\)?)?\s*[:\-]?\s*([^\n]{3,160})/i,
      /(?:name(?:s)?\s+of\s+tenant(?:s)?|tenant(?:s)?\s+name(?:s)?)\s*[:\-]?\s*([^\n]{3,160})/i,
      /(?:tenant\s+signature(?:\(s\))?)\s*[:\-]?\s*([A-Za-z ,.'-]{3,120})/i,
    ])
  );
  const suiteFromText = cleanupSuiteCandidate(
    pickFirstMatch(text, [
      /(?:suite|unit|apartment|apt\.?)\s*(?:number|no\.?|#)?\s*[:\-]?\s*([A-Za-z0-9\-]+)/i,
      /rental\s+unit\s*[:\-]?\s*([A-Za-z0-9\-]+)/i,
      /premises(?:\s+located\s+at)?\s*[:\-]?\s*(?:unit|suite|apt\.?|apartment)?\s*#?\s*([A-Za-z0-9\-]+)/i,
    ])
  );

  const startDateFromText =
    pickDateByLabels(text, [
      "start date",
      "commencement date",
      "lease start",
      "term starts",
      "tenancy starts",
      "move-in date",
      "from",
    ]) ||
    tryParseDate(
      pickFirstMatch(text, [
        /(?:start\s+date|commencement\s+date|move[- ]in\s+date|lease\s+start|term\s+starts?)\s*[:\-]?\s*([^\n]{6,60})/i,
        /from\s+([A-Za-z0-9,\/\-\s]{6,40})\s+(?:to|until|through)/i,
      ])
    );

  const endDateFromText =
    pickDateByLabels(text, [
      "end date",
      "lease end",
      "expiry date",
      "term ends",
      "fixed term end",
      "to",
      "until",
      "through",
    ]) ||
    tryParseDate(
      pickFirstMatch(text, [
        /(?:end\s+date|lease\s+end|expiry\s+date|term\s+ends?|fixed\s+term\s+end)\s*[:\-]?\s*([^\n]{6,60})/i,
        /(?:to|until|through)\s+([A-Za-z0-9,\/\-\s]{6,40})/i,
      ])
    );

  const allDates = collectDateValues(text);
  const fallbackStartDate = startDateFromText || allDates[0] || "";
  const fallbackEndDate =
    endDateFromText ||
    allDates.find((d) => d && d !== fallbackStartDate) ||
    "";

  const baseRentFromText = parseMoney(
    pickFirstMatch(text, [
      /(?:base\s+rent|monthly\s+rent|lawful\s+rent|rent\s+amount|rent\s+payable|total\s+monthly\s+rent)\s*[:\-]?\s*\$?\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i,
      /\$?\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)\s*(?:per\s+month|monthly)/i,
    ])
  );

  const securityDepositFromText = parseMoney(
    pickFirstMatch(text, [
      /(?:security\s+deposit|rent\s+deposit|damage\s+deposit|deposit\s+amount|last\s+month'?s\s+rent\s+deposit)\s*[:\-]?\s*\$?\s*([0-9][0-9,]*(?:\.[0-9]{1,2})?)/i,
    ])
  );

  const tenantEmailFromText = pickEmail(text);
  const rentDayFromText = parseRentDay(
    pickFirstMatch(text, [
      /rent(?:\s+is)?\s+due(?:\s+on)?\s*(?:the)?\s*([0-9]{1,2}(?:st|nd|rd|th)?|[A-Za-z-]+)/i,
      /rent\s+day\s*[:\-]?\s*([0-9]{1,2}|[A-Za-z-]+)/i,
      /due\s+date\s*[:\-]?\s*([0-9]{1,2}|[A-Za-z-]+)/i,
    ])
  );

  const tenantFull = formTenantFull || tenantFullFromText || "";
  const tenantSplit = splitName(tenantFull);
  const suiteNumber = formSuiteNumber || suiteFromText || "";
  const startDate = formStartDate || fallbackStartDate || "";
  const endDate = formEndDate || fallbackEndDate || "";
  const baseRent = formBaseRent > 0 ? formBaseRent : baseRentFromText;
  const securityDeposit =
    formSecurityDeposit > 0 ? formSecurityDeposit : securityDepositFromText;
  const tenantEmail = formTenantEmail || tenantEmailFromText || "";
  const rentDay = formRentDay || rentDayFromText || "";

  const warnings = [];
  if (!suiteNumber) warnings.push("Suite/Unit was not confidently extracted.");
  if (!tenantSplit.full) warnings.push("Tenant name was not confidently extracted.");
  if (!baseRent) warnings.push("Rent amount was not confidently extracted.");
  if (!startDate) warnings.push("Start date was not confidently extracted.");
  if (!Object.keys(formFields || {}).length) {
    warnings.push("No embedded PDF form fields were found; extraction used text/OCR heuristics.");
  }

  const fieldConfidence = {
    source: detected.confidence,
    suiteNumber: formSuiteNumber ? 0.95 : suiteFromText ? 0.72 : 0.2,
    tenantName: formTenantFull ? 0.96 : tenantFullFromText ? 0.7 : 0.2,
    tenantEmail: formTenantEmail ? 0.95 : tenantEmailFromText ? 0.65 : 0.25,
    startDate: formStartDate ? 0.95 : startDate ? 0.67 : 0.2,
    endDate: formEndDate ? 0.92 : endDate ? 0.58 : 0.25,
    baseRent: formBaseRent > 0 ? 0.95 : baseRent > 0 ? 0.72 : 0.2,
    securityDeposit:
      formSecurityDeposit > 0 ? 0.95 : securityDeposit > 0 ? 0.62 : 0.35,
    rentDay: formRentDay ? 0.9 : rentDay ? 0.58 : 0.25,
  };

  return {
    detected,
    draft: {
      source: detected.source,
      jurisdiction: detected.jurisdiction || null,
      suiteNumber: suiteNumber || "",
      tenantFirstName: tenantSplit.first || "",
      tenantLastName: tenantSplit.last || "",
      tenantFullName: tenantSplit.full || "",
      tenantEmail: tenantEmail || "",
      startDate: startDate || "",
      endDate: endDate || "",
      baseRent: baseRent > 0 ? String(baseRent) : "",
      securityDeposit: securityDeposit > 0 ? String(securityDeposit) : "",
      rentDay: rentDay || "",
      additionalNotes: "",
      extractedTextPreview: text.slice(0, 10000),
    },
    warnings,
    fieldConfidence,
  };
}

function composeAddressFromProperty(prefix, propertyData) {
  const p = propertyData || {};
  if (prefix === "landlord") {
    if (safeStr(p.landlordaddress)) return safeStr(p.landlordaddress);
    return [
      p.landlordunitnumber,
      p.landlordstreetnumber,
      p.landlordstreetname,
      p.landlordcity,
      p.landlordprovince,
      p.landlordpostalcode,
      p.landlordpobox,
    ]
      .map((v) => safeStr(v))
      .filter(Boolean)
      .join(" ");
  }

  if (safeStr(p.manageraddress)) return safeStr(p.manageraddress);
  return [
    p.managerunitnumber,
    p.managerstreetnumber,
    p.managerstreetname,
    p.managercity,
    p.managerprovince,
  ]
    .map((v) => safeStr(v))
    .filter(Boolean)
    .join(" ");
}

export function buildCanonicalImportInput({
  draft,
  propertyId,
  propertyDoc,
  uploadedFileUrl,
  uploadedFilename,
  createdDatePart,
}) {
  const d = draft || {};
  const source = safeStr(d.source).toLowerCase() || "monthly";
  const tenantFull =
    safeStr(d.tenantFullName) ||
    normalizeWhitespace(`${safeStr(d.tenantFirstName)} ${safeStr(d.tenantLastName)}`);
  const tenantFirst = safeStr(d.tenantFirstName) || splitName(tenantFull).first;
  const tenantLast = safeStr(d.tenantLastName) || splitName(tenantFull).last;
  const tenantEmail = safeStr(d.tenantEmail);
  const startDate = safeStr(d.startDate) || createdDatePart || "";
  const endDate = safeStr(d.endDate);
  const suiteNumber = safeStr(d.suiteNumber);
  const baseRent = safeStr(d.baseRent);
  const securityDeposit = safeStr(d.securityDeposit);
  const rentDay = safeStr(d.rentDay);
  const p = propertyDoc || {};

  if (source === "ontario") {
    const formData = {
      status: "executed",
      leaseId: "",
      landlordName: safeStr(p.name || p.landlordname),
      tenantEmail,
      tenantEmails: tenantEmail ? [tenantEmail] : [],
      emailConsent: null,
      emergencyContact: null,
      tenantfirstname: [tenantFirst || tenantFull],
      tenantlastname: [tenantLast || ""],
      suiteNumber,
      isCondo: null,
      parkingCount: null,
      parkingstalls: [],
      startDate,
      leaseType: endDate ? "fixed" : "monthly",
      fixedEndDate: endDate || "",
      otherLeaseType: "",
      rentDay,
      rentDueType: "monthly",
      rentDueOther: "",
      baseRent,
      parkingRent: "",
      otherServicesRent: [],
      rentAmount: toNumber(baseRent),
      rentPayableTo: safeStr(p.name || p.landlordname),
      howToPayRent: [],
      partialRent: "",
      partialRentDate: "",
      nsfCharge: "",
      gasincluded: "YES",
      airincluded: "YES",
      storageincluded: "YES",
      laundryincluded: "YES",
      guestparkingincluded: "YES",
      otherincluded: [],
      additionalservicenotes: "",
      electricityresponsibility: "LANDLORD",
      heatresponsibility: "LANDLORD",
      waterresponsibility: "LANDLORD",
      rentdiscount: "NO",
      rentdiscountnotes: "",
      depositrequired: toNumber(securityDeposit) > 0 ? "YES" : "NO",
      securityDepositAmount: toNumber(securityDeposit) > 0 ? securityDeposit : "",
      keydeposit: "NO",
      keydepositamount: "",
      keydepositinformation: "",
      smokingpolicy: "YES",
      smokingpolicydetails: "",
      tenantinsurance: "YES",
      additionalterms: "NO",
      additionaldocuments: [],
      leaseAddendumsSelectedIds: [],
      customLeaseAddendumCatalog: [],
      landlordsignaturename: [""],
      landlordsignature: [null],
      landlordsignaturedate: [createdDatePart],
      tenantsignaturename: [tenantFull || ""],
      tenantsignature: [null],
      tenantsignaturedate: [createdDatePart],
    };

    return {
      source: "ontario",
      formData,
      tenantEmails: tenantEmail ? [tenantEmail] : [],
      activeProperty: propertyId,
      activePropertyData: propertyDoc,
      pdfFilename: uploadedFilename,
      url: uploadedFileUrl,
      lifecycleKey: "executed",
      resolvedStartDate: startDate || null,
      resolvedEndDate: endDate || null,
      resolvedTenantName: tenantFull || null,
    };
  }

  if (source === "manitoba" || source === "alberta") {
    const fd = {
      leasetype: source === "manitoba" ? "Manitoba" : "Alberta",
      landlordSnapshot: {
        landlordname: safeStr(p.landlordname || p.name),
        landlordunitnumber: safeStr(p.landlordunitnumber),
        landlordstreetnumber: safeStr(p.landlordstreetnumber),
        landlordstreetname: safeStr(p.landlordstreetname),
        landlordcity: safeStr(p.landlordcity),
        landlordprovince: safeStr(p.landlordprovince || p.rentalprovince),
        landlordpostalcode: safeStr(p.landlordpostalcode),
        landlordpobox: safeStr(p.landlordpobox),
        landlordphone: safeStr(p.landlordphone),
        landlordemail: safeStr(p.landlordemail),
      },
      hasagent: !!safeStr(p.propertymanager),
      managerSnapshot: {
        propertymanager: safeStr(p.propertymanager),
        managerunitnumber: safeStr(p.managerunitnumber),
        managerstreetnumber: safeStr(p.managerstreetnumber),
        managerstreetname: safeStr(p.managerstreetname),
        managercity: safeStr(p.managercity),
        managerprovince: safeStr(p.managerprovince),
        managerphone: safeStr(p.managerphone),
        manageremail: safeStr(p.manageremail),
      },
      tenantfirstname: [tenantFirst || tenantFull],
      tenantlastname: [tenantLast || ""],
      tenantaddress: [""],
      tenantphone: [""],
      tenantemail: [tenantEmail],
      tenantEmails: tenantEmail ? [tenantEmail] : [],
      suiteNumber,
      isCondo: false,
      tenancyterm: endDate ? "fixed" : "periodic",
      startDate,
      endDate: endDate || "",
      periodictype: "monthly",
      periodicothernotes: "",
      securityDepositAmount: securityDeposit || "",
      securityDepositPaidOn: "",
      petDepositAmount: "",
      petDepositPaidOn: "",
      rentDay: rentDay || "1",
      rentDueType: "MONTH",
      baseRent: baseRent || "",
      parkingspaces: "",
      parkingRent: "",
      otherRentType: [],
      otherRent: [],
      discountRent: "0",
      rentDueOther: "",
      payableRent: toNumber(baseRent),
      rentAmount: toNumber(baseRent),
      landlordsignaturename: [safeStr(p.landlordname || p.name)],
      landlordsignature: [null],
      landlordsignaturedate: [createdDatePart],
      tenantsignaturename: [tenantFull || ""],
      tenantsignature: [null],
      tenantsignaturedate: [createdDatePart],
      leaseAddendumsSelectedIds: [],
      customLeaseAddendumCatalog: [],
      status: "executed",
    };

    return {
      source,
      fd,
      tenantEmails: tenantEmail ? [tenantEmail] : [],
      activeProperty: propertyId,
      activePropertyData: propertyDoc,
      pdfFilename: uploadedFilename,
      url: uploadedFileUrl,
      lifecycleKey: "executed",
      leaseAddendums: [],
    };
  }

  // Default generic (monthly/fixed SK flow)
  const normalizedSource = source === "fixed" ? "fixed" : "monthly";
  const formData = {
    tenantName: tenantFull,
    tenantMail: "",
    suiteNumber,
    todayDate: createdDatePart,
    landlordSnapshot: {
      landlordname: safeStr(p.landlordname || p.name),
      landlordaddress: composeAddressFromProperty("landlord", p),
      landlordphone: safeStr(p.landlordphone),
      landlordemail: safeStr(p.landlordemail),
    },
    managerSnapshot: {
      propertymanager: safeStr(p.propertymanager),
      manageraddress: composeAddressFromProperty("manager", p),
      managerphone: safeStr(p.managerphone),
      manageremail: safeStr(p.manageremail),
    },
    rentPayableTo: safeStr(p.landlordemail || p.manageremail || p.name),
    moveInDate: startDate,
    tenantPhone: "",
    tenantEmail,
    emergencyContactName: "",
    emergencyContactPhone: "",
    rentAmount: baseRent || "",
    rentDay: rentDay || "",
    rentStartDate: startDate,
    prorated: false,
    proratedAmount: "",
    proratedStartDay: "",
    damageDeposit: "",
    leaseTerm: normalizedSource === "fixed" ? "12" : "",
    appliances: {
      fridge: false,
      stove: false,
      microwave: false,
      ac: false,
      dishwasher: false,
    },
    services: {
      electricity: false,
      heat: false,
      water: false,
      hotWater: false,
      parking: false,
      range: false,
      refrigerator: false,
      washerDryer: false,
      washerDryerCoin: false,
      cableTVHookup: false,
      cableTVService: false,
      other: "",
    },
    securityDepositRequired: toNumber(securityDeposit) > 0,
    securityDepositAmount: toNumber(securityDeposit) > 0 ? securityDeposit : "",
    securityDepositDueDate: "",
    hasPets: false,
    pet1: { type: "", name: "", notes: "", agreed: false },
    pet2: { type: "", name: "", notes: "", agreed: false },
    agreesToSmokingPolicy: false,
    tenantSignatureName: tenantFull || "",
    managerSignatureName: safeStr(p.propertymanager || p.landlordname || p.name),
    status: "executed",
    tenantEmails: tenantEmail ? [tenantEmail] : [],
    leaseAddendumsSelectedIds: [],
    customLeaseAddendumCatalog: [],
  };

  return {
    source: normalizedSource,
    formData,
    activeProperty: propertyId,
    activePropertyData: propertyDoc,
    pdfFilename: uploadedFilename,
    url: uploadedFileUrl,
    createdDatePart,
    lifecycleKey: "executed",
  };
}

export function validateLeaseImportDraft(draft) {
  const d = draft || {};
  const errors = [];
  if (!safeStr(d.source)) errors.push("Lease source is required.");
  if (!safeStr(d.suiteNumber)) errors.push("Suite/Unit is required.");
  if (!safeStr(d.tenantFullName) && !safeStr(d.tenantFirstName)) {
    errors.push("At least one tenant name is required.");
  }
  if (!safeStr(d.startDate)) errors.push("Start date is required.");
  if (toNumber(d.baseRent) <= 0) errors.push("Rent amount must be greater than 0.");
  return {
    ok: errors.length === 0,
    errors,
  };
}
